/**
 * cmHome — the CM home page's PURE policy layer (ordering rules, severity
 * classes, prose dates, screen-language labels). No fetches, no React:
 * everything here is unit-testable, and the ordering rules are the
 * DESIGN RULINGS (brief of record 2026-07-06, R4/R4a/R4b) written as
 * code:
 *
 *   R4a  attention band = severity class (clinical currency → billing
 *        blockers → the rest), oldest-first within class. A card takes
 *        the class of its HIGHEST member (max, never average).
 *   R4   badge counts CARDS; cap 5; overflow "+N more" grouped by client.
 *   R4b  within the client list's pending-attention group, the sort
 *        MIRRORS the attention band's order.
 *   R2   screen language: "record update" never proposal/extraction;
 *        "needs more detail" never kickback; the pipeline is invisible.
 *
 * Age is prose (day names), never raw timestamps.
 */

import type {
  CmHomeAttentionWire,
  CmHomeClientWire,
  CmHomeRailLineWire,
  CmHomeSnapshot,
  CmHomeSpiceAttentionWire,
  CmHomeSpiceRowWire,
} from '@/lib/cmHomeApi';
import { formatSpiceAwayOfStatus, formatSpiceSlot } from '@/lib/spiceScheduleCopy';
// Prose-date helpers now live in the shared lib; cmHome re-exports the public
// ones so its existing importers (home components) need no edits.
import {
  WEEKDAYS,
  MONTHS,
  parseIsoDate,
  daysBetween,
  proseDay,
  proseUpcomingDay,
  yourNotePhrase,
  fmtTime,
} from '@/lib/proseDate';
import { practiceDayOfStamp } from '@/lib/practiceDate';
export { daysBetween, proseDay, yourNotePhrase } from '@/lib/proseDate';

/** The practice's day of a stamp on the home read (a client's last touch, a
 *  rail line): the clock the page's today reads (practiceTodayIso), so a
 *  line from 11 PM in New York belongs to that New York day on every device.
 *  '' for text that is not a date. */
export function homeDayOf(at: string): string {
  return practiceDayOfStamp(at) ?? '';
}

// ---------------------------------------------------------------------
// R4a — the severity-class table (all 16 update types + the kickback).
// ---------------------------------------------------------------------

/** Class 1 — clinical currency: unconfirmed changes to clinical truth. */
const CLASS_1_TYPES: ReadonlySet<string> = new Set([
  'medication_add',
  'medication_change',
  'medication_discontinue',
  'condition_add',
  'condition_change',
  'risk_change',
  'hospital_event',
  'functional_status_change',
  // allergy_change: the 16th type (0136, WS-3) — patient-safety truth.
  'allergy_change',
]);

export type SeverityClass = 1 | 2 | 3;

/**
 * A card takes the class of its HIGHEST member — max, never average
 * (R4a bundling rule). Kickbacks + failed extractions are the CM's
 * money-path duties: class 2.
 */
export function severityClassFor(card: CmHomeAttentionWire): SeverityClass {
  if (card.kind === 'needs_detail' || card.kind === 'extraction_failed') return 2;
  const types = Object.keys(card.type_counts ?? {});
  return types.some((t) => CLASS_1_TYPES.has(t)) ? 1 : 3;
}

/** Severity class ascending, then oldest-first within class (R4a). */
export function compareAttention(a: CmHomeAttentionWire, b: CmHomeAttentionWire): number {
  const cls = severityClassFor(a) - severityClassFor(b);
  if (cls !== 0) return cls;
  if (a.anchor_at !== b.anchor_at) return a.anchor_at < b.anchor_at ? -1 : 1;
  return a.note_id < b.note_id ? -1 : a.note_id > b.note_id ? 1 : 0;
}

export function orderAttention(cards: readonly CmHomeAttentionWire[]): CmHomeAttentionWire[] {
  return [...cards].sort(compareAttention);
}

/**
 * The ONE attention-card headline, shared by the Clients attention band
 * and the Hub's compact glance (founder walk 2026-07-06: one derived
 * system, two surfaces — never two copies of the copy).
 */
export function attentionCardTitle(card: CmHomeAttentionWire, todayIso: string): string {
  const phrase = yourNotePhrase(card.note_occurred_on, todayIso);
  // "your Tuesday note" -> "Your Tuesday note"
  const capPhrase = phrase.charAt(0).toUpperCase() + phrase.slice(1);
  if (card.kind === 'needs_detail') {
    return `${capPhrase} on ${card.client_display_name} needs more detail`;
  }
  if (card.kind === 'extraction_failed') {
    return `${capPhrase} on ${card.client_display_name} didn't go through`;
  }
  const n = card.pending_count ?? 0;
  return `Confirm ${n} record ${n === 1 ? 'update' : 'updates'} for ${card.client_display_name}`;
}

export const ATTENTION_CARD_CAP = 5;

/** Overflow beyond the cap, grouped by client (R4): name → hidden-card count. */
export function overflowByClient(
  ordered: readonly CmHomeAttentionWire[],
  cap: number = ATTENTION_CARD_CAP,
): Array<{ client_display_name: string; count: number }> {
  const grouped = new Map<string, number>();
  for (const card of ordered.slice(cap)) {
    grouped.set(card.client_display_name, (grouped.get(card.client_display_name) ?? 0) + 1);
  }
  return [...grouped.entries()].map(([client_display_name, count]) => ({
    client_display_name,
    count,
  }));
}

// ---------------------------------------------------------------------
// R4/R4b — the client-list ordering: today's visits → pending attention
// (mirroring the band, R4b) → last-touched desc → staleness tiebreak.
// ---------------------------------------------------------------------

export function orderClients(
  clients: readonly CmHomeClientWire[],
  attention: readonly CmHomeAttentionWire[],
): CmHomeClientWire[] {
  // The best (band-ordered) card per client — R4b: the pending-attention
  // group mirrors the attention band's own order.
  const bestCard = new Map<string, CmHomeAttentionWire>();
  for (const card of orderAttention(attention)) {
    if (!bestCard.has(card.client_id)) bestCard.set(card.client_id, card);
  }

  function groupOf(c: CmHomeClientWire): number {
    if (c.visit_today) return 0;
    if (bestCard.has(c.id)) return 1;
    return 2;
  }

  return [...clients].sort((a, b) => {
    const g = groupOf(a) - groupOf(b);
    if (g !== 0) return g;
    const group = groupOf(a);
    if (group === 0) {
      // The appointments projection is date-granular (no time of day), so
      // "by time" has nothing to sort on — name is the stable stand-in.
      return a.display_name.localeCompare(b.display_name);
    }
    if (group === 1) {
      const cmp = compareAttention(bestCard.get(a.id)!, bestCard.get(b.id)!);
      if (cmp !== 0) return cmp;
    }
    // Last-touched desc, never-touched last; ties by name (staleness IS
    // the inverse of last-touched, so the tiebreak falls out of it).
    const ta = a.last_touched_at;
    const tb = b.last_touched_at;
    if (ta !== tb) {
      if (ta === null) return 1;
      if (tb === null) return -1;
      return ta > tb ? -1 : 1;
    }
    return a.display_name.localeCompare(b.display_name);
  });
}

// ---------------------------------------------------------------------
// R2 — screen-language labels (terminology dictionary).
// ---------------------------------------------------------------------

/** Update-type → screen noun [singular, plural]; families collapse. */
const TYPE_NOUNS: ReadonlyArray<[RegExp, [string, string]]> = [
  [/^medication_/, ['medication', 'medications']],
  [/^appointment_/, ['appointment', 'appointments']],
  [/^condition_/, ['condition', 'conditions']],
  [/^risk_change$/, ['risk flag', 'risk flags']],
  [/^provider_new$/, ['provider', 'providers']],
  [/^contact_change$/, ['contact', 'contacts']],
  [/^hospital_event$/, ['hospital event', 'hospital events']],
  [/^directive_change$/, ['directive', 'directives']],
  [/^coverage_change$/, ['coverage update', 'coverage updates']],
  [/^housing_change$/, ['housing update', 'housing updates']],
  [/^functional_status_change$/, ['daily activity update', 'daily activity updates']],
  [/^allergy_change$/, ['allergy', 'allergies']],
];

/** "1 medication, 1 appointment, 1 risk flag" (family counts collapse). Caps
 *  at THREE named kinds + a remainder clause ("…, and 7 more kinds") so a
 *  broad note never spills ten clauses onto the card (DL v1). */
export function typeSummary(typeCounts: Readonly<Partial<Record<string, number>>>): string {
  const byNoun = new Map<string, { plural: string; count: number }>();
  const order: string[] = [];
  for (const [type, count] of Object.entries(typeCounts)) {
    if (!count) continue;
    const hit = TYPE_NOUNS.find(([re]) => re.test(type));
    const [singular, plural] = hit ? hit[1] : ['record update', 'record updates'];
    const entry = byNoun.get(singular);
    if (entry) {
      entry.count += count;
    } else {
      byNoun.set(singular, { plural, count });
      order.push(singular);
    }
  }
  const NAMED_CAP = 3;
  const clauses = order.slice(0, NAMED_CAP).map((singular) => {
    const { plural, count } = byNoun.get(singular)!;
    return `${count} ${count === 1 ? singular : plural}`;
  });
  const moreKinds = order.length - NAMED_CAP;
  if (moreKinds > 0) {
    clauses.push(`and ${moreKinds} more ${moreKinds === 1 ? 'kind' : 'kinds'}`);
  }
  return clauses.join(', ');
}

/** record_events entity → the record section name the rail speaks. */
export const ENTITY_LABELS: Readonly<Record<string, string>> = {
  medication: 'Medications',
  appointment: 'Appointments',
  condition: 'Conditions',
  housing: 'Housing',
  risk: 'Risk flags',
  provider: 'Providers',
  contact: 'Contacts',
  hospital: 'Hospital events',
  directive: 'Directives',
  // WS-8 rename (founder 2026-07-10): the insurance section reads
  // "Insurance" on every user-facing surface; 'coverage' stays the wire token.
  coverage: 'Insurance',
  functional_status: 'Daily activities',
  allergy: 'Allergies',
  note: 'Notes',
};

/** Risk-category vocab token → pill text ('med_mgmt' → 'Med mgmt'). */
export function riskCategoryLabel(token: string): string {
  const spaced = token.replace(/_/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Actor display for the rail: office roles collapse to "Office" (the
 *  mockup's voice); supervising CMs carry the parenthetical. */
export function actorLabel(name: string, role: string | null): string {
  if (role === 'admin_support' || role === 'exec_support' || role === 'data_steward') {
    return 'Office';
  }
  if (role === 'supervising_cm') return `${name} (supervising)`;
  return name;
}

// ---------------------------------------------------------------------
// Row status derivations (band 2).
// ---------------------------------------------------------------------

/** Days quiet before the row says so (the config-seam fallback). */
export const QUIET_DAYS_THRESHOLD = 10;

/**
 * Read the tenant quiet-days threshold from tenants.config
 * (`config.features.quiet_days_threshold`: a positive integer of days),
 * falling back to QUIET_DAYS_THRESHOLD when unset/malformed
 * (COMMERCIALIZATION D2 config seam; cm_daily_cap read-with-fallback
 * precedent). Pure; unit-tested.
 */
export function readQuietDaysThreshold(config: unknown): number {
  if (typeof config !== 'object' || config === null) return QUIET_DAYS_THRESHOLD;
  const features = (config as Record<string, unknown>).features;
  if (typeof features !== 'object' || features === null) return QUIET_DAYS_THRESHOLD;
  const v = (features as Record<string, unknown>).quiet_days_threshold;
  return typeof v === 'number' && Number.isInteger(v) && v > 0 ? v : QUIET_DAYS_THRESHOLD;
}

export function quietDays(client: CmHomeClientWire, todayIso: string): number | null {
  if (client.last_touched_at === null) return null;
  return daysBetween(homeDayOf(client.last_touched_at), todayIso);
}

/**
 * The row's attention ECHO — never a pending-update count (the band owns
 * those; R4 "rows never double-report").
 */
export function attentionEcho(
  clientId: string,
  attention: readonly CmHomeAttentionWire[],
): string | null {
  if (attention.some((a) => a.client_id === clientId && a.kind === 'needs_detail')) {
    return 'Note needs detail';
  }
  if (attention.some((a) => a.client_id === clientId && a.kind === 'extraction_failed')) {
    return "A note didn't go through. Open the client to try it again.";
  }
  return null;
}

/**
 * Visit-day echo: an event today/recent by others touching a client who
 * has a visit today surfaces as a small status echo on their row.
 */
/** The visit-today marker: "Visit today at 2:30 PM" for the earliest
 *  timed visit of the day, "Visit today" when none carries a time. */
export function visitTodayLabel(client: Pick<CmHomeClientWire, 'visit_today_time'>): string {
  return client.visit_today_time ? `Visit today at ${fmtTime(client.visit_today_time)}` : 'Visit today';
}

export function visitDayEcho(
  client: CmHomeClientWire,
  rail: readonly CmHomeRailLineWire[],
  todayIso: string,
): string | null {
  if (!client.visit_today) return null;
  const recent = rail.find(
    (l) => l.client_id === client.id && daysBetween(homeDayOf(l.at), todayIso) <= 2,
  );
  if (!recent) return null;
  const section = recent.entity ? (ENTITY_LABELS[recent.entity] ?? 'Record') : 'Record';
  const when = proseDay(homeDayOf(recent.at), todayIso);
  return `${section} updated ${when}`;
}

/**
 * The rail lines dated today (the practice's day): the "From your team" heading's
 * count. The rail itself keeps its most recent lines regardless of day,
 * so this is a subset of what renders beneath the heading.
 */
export function railLinesToday(rail: readonly CmHomeRailLineWire[], todayIso: string): number {
  return rail.filter((l) => homeDayOf(l.at) === todayIso).length;
}

/**
 * The row's last-activity line, care-manager path: a SPICE session
 * never reads as a care-management visit.
 *
 * A row carrying the SPICE template (spice_row: an assigned therapist)
 * tells its SPICE story there — the chip names the therapist and slot,
 * the status phrase names the next session or the plan's status — so
 * this line speaks only the care manager's own contact: "Visited {day}"
 * from the care-management-band signal (last_note_occurred_on, already
 * restricted to those bands server-side), or nothing. A weekly client's
 * last session shares its weekday with the next one, so a second SPICE
 * phrase here ("SPICE session Tuesday" beside "Next session Tuesday")
 * would read as the same fact said twice.
 *
 * With no template on the row, the client's most recent SPICE-band
 * note, when newer than (or the only) counted activity, reads "SPICE
 * session {day}" ("(declined)" appended for a decline) — the one place
 * that fact would otherwise pass for a visit. Null when there is
 * nothing to say (no note of either kind yet). Pure; the caller gates
 * this behind its own isNew/quiet/visit-today checks.
 */
export function lastActivityLine(client: CmHomeClientWire, todayIso: string): string | null {
  const spice = client.spice_row === null ? (client.last_spice_note ?? null) : null;
  const cmDate = client.last_note_occurred_on;
  const spiceIsNewer = spice !== null && (cmDate === null || spice.occurred_on > cmDate);
  if (spiceIsNewer) {
    return `SPICE session ${proseDay(spice.occurred_on, todayIso)}${spice.declined ? ' (declined)' : ''}`;
  }
  if (cmDate !== null) {
    return `Visited ${proseDay(cmDate, todayIso)}`;
  }
  return null;
}

/**
 * The unified SPICE row template's chip: "SPICE · <therapist> · Tue
 * 2:00 PM" when a next session is known, else just "SPICE ·
 * <therapist>" (assigned but nothing scheduled yet). The therapist is
 * the next session's own when it has one, else the client's. The day
 * and time read through the shared SPICE copy (formatSpiceSlot).
 */
export function spiceRowChipText(row: CmHomeSpiceRowWire): string {
  if (row.next_session === null) return `SPICE · ${row.therapist_name}`;
  const slot = formatSpiceSlot({ weekday: row.next_session.weekday, start_time: row.next_session.start_time });
  return `SPICE · ${row.next_session.therapist_name ?? row.therapist_name} · ${slot}`;
}

/**
 * The row's trailing status text, following the chip: the away phrase
 * when the plan is covered or paused ("Covered by Amara Lindenbrook until
 * 14 Oct", "Paused until 19 Sep (Vacation)", through the shared
 * formatSpiceAwayOfStatus), else "Next session {day}" naming the
 * nearest upcoming occurrence in words that read "this week" as itself
 * and an every-two-weeks client's off-week gap as a real date
 * (proseUpcomingDay). "No schedule on file yet" for an assigned
 * therapist with nothing saved (cmHome.ts's own "active with no
 * next_session" convention).
 */
export function spiceRowStatusText(row: CmHomeSpiceRowWire, todayIso: string): string {
  if (row.status !== 'active') return formatSpiceAwayOfStatus({ ...row, cover_name: row.cover_name ?? null }, todayIso);
  if (row.next_session === null) return 'No schedule on file yet';
  return `Next session ${proseUpcomingDay(row.next_session.date, todayIso)}`;
}

/**
 * The missed-note attention item's headline: "Thursday's session with
 * Vito has no note yet." `visit_on` has already passed (the caller's
 * own gate, buildSpiceAttention), so proseDay's past-oriented register
 * is exactly right here — never proseUpcomingDay.
 */
export function spiceAttentionTitle(item: CmHomeSpiceAttentionWire, todayIso: string): string {
  const day = proseDay(item.visit_on, todayIso);
  const capDay = day.charAt(0).toUpperCase() + day.slice(1);
  return `${capDay}'s session with ${item.client_display_name} has no note yet.`;
}

// ---------------------------------------------------------------------
// R8 — the header + Case Note default: nearest upcoming visit's client
// today, else last-touched.
// ---------------------------------------------------------------------

export function defaultComposeClient(
  clients: readonly CmHomeClientWire[],
): CmHomeClientWire | null {
  const visits = clients
    .filter((c) => c.visit_today)
    .sort((a, b) => a.display_name.localeCompare(b.display_name));
  if (visits.length > 0) return visits[0]!;
  const touched = clients
    .filter((c) => c.last_touched_at !== null)
    .sort((a, b) => (a.last_touched_at! > b.last_touched_at! ? -1 : 1));
  if (touched.length > 0) return touched[0]!;
  return clients[0] ?? null;
}

// ---------------------------------------------------------------------
// Header helpers.
// ---------------------------------------------------------------------

/** First name from a stored staff name (an edit or void author on a note). */
export function firstNameFrom(details: string | null | undefined): string {
  if (!details) return '';
  let value = details.trim();
  const at = value.indexOf('@');
  if (at > 0) value = value.slice(0, at);
  const token = value.split(/[\s._-]+/).filter(Boolean)[0] ?? '';
  if (!token) return '';
  return token.charAt(0).toUpperCase() + token.slice(1);
}

export function greetingForHour(hour: number): string {
  if (hour < 5) return 'Hello';
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

/** The DEVICE's calendar date, ISO. Not "today" on My Clients or anything
 *  reading the home read: those read the practice's day (practiceTodayIso,
 *  src/lib/practiceDate.ts), the clock isOverdue and the server use. */
export function localTodayIso(now: Date = new Date()): string {
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${mm}-${dd}`;
}

/** "Monday, July 6" for the orientation line. */
export function orientationDate(todayIso: string): string {
  const d = parseIsoDate(todayIso);
  return `${WEEKDAYS[d.getDay()]}, ${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

/**
 * The day's visit count the hero speaks. A care manager's is the
 * appointments projection (visits_today_count). A SPICE-only viewer's
 * is her planned sessions today from My week: the projection is
 * care-management scheduling and says nothing about her day. A day
 * outside the week (a weekend read) has no sessions to count. Either
 * way the viewer's own planned care visits today count too; another
 * care manager's visit to her client does not.
 */
export function visitsTodayCount(
  home: Pick<CmHomeSnapshot, 'visits_today_count' | 'my_week' | 'care_visits_today'>,
  todayIso: string,
): number {
  const ownVisits = (home.care_visits_today ?? []).filter((v) => v.is_mine).length;
  if (home.my_week != null) {
    return (home.my_week.days.find((d) => d.date === todayIso)?.visits.length ?? 0) + ownVisits;
  }
  return home.visits_today_count + ownVisits;
}

/** "2 visits today" / "1 visit today"; zero is a calm fact, never an apology. */
export function visitsTodayPhrase(count: number): string {
  if (count === 0) return 'a clear calendar so far';
  return `${count} ${count === 1 ? 'visit' : 'visits'} today`;
}

/** The hero's purpose line: "Monday, July 6 · 2 visits today". */
export function heroPurposeLine(todayIso: string, visitsToday: number): string {
  return `${orientationDate(todayIso)} · ${visitsTodayPhrase(visitsToday)}`;
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}
